import type {
  CanvasSectionBlock,
  CanvasSectionBlockLayout,
} from '../sectionRegistry';

export type ResponsiveViewport = 'desktop' | 'tablet' | 'mobile';

export const TOTAL_COLUMNS = 24;
export const FREE_POSITION_OFFSET_LIMIT_PERCENT = 2000;
export const RESPONSIVE_LAYOUT_MOBILE_BREAKPOINT_PX = 640;
export const RESPONSIVE_LAYOUT_TABLET_BREAKPOINT_PX = 1024;

export function resolveResponsiveLayoutBreakpoint(width: number): ResponsiveViewport {
  if (width > 0 && width < RESPONSIVE_LAYOUT_MOBILE_BREAKPOINT_PX) return 'mobile';
  if (width > 0 && width < RESPONSIVE_LAYOUT_TABLET_BREAKPOINT_PX) return 'tablet';
  return 'desktop';
}

export function resolveResponsiveBlockLayout(
  block: CanvasSectionBlock,
  adminViewport?: ResponsiveViewport
): CanvasSectionBlockLayout {
  const viewport =
    adminViewport ??
    (typeof window === 'undefined'
      ? 'desktop'
      : resolveResponsiveLayoutBreakpoint(window.innerWidth));
  if (viewport === 'mobile') {
    return block.responsiveLayout?.mobile ?? block.layout;
  }
  if (viewport === 'tablet') {
    return block.responsiveLayout?.tablet ?? block.layout;
  }
  return block.layout;
}

/** Snapshot desktop grid into tablet/mobile before a desktop-only layout edit. */
export function forkResponsiveLayoutsBeforeDesktopLayoutEdit(
  block: CanvasSectionBlock,
  nextDesktopLayout: CanvasSectionBlockLayout
): CanvasSectionBlock {
  const responsive = block.responsiveLayout ?? {};
  const needsTabletFork = !responsive.tablet;
  const needsMobileFork = !responsive.mobile;
  if (!needsTabletFork && !needsMobileFork) {
    return { ...block, layout: nextDesktopLayout };
  }
  const priorDesktopLayout = block.layout;
  return {
    ...block,
    layout: nextDesktopLayout,
    responsiveLayout: {
      ...responsive,
      ...(needsTabletFork ? { tablet: { ...priorDesktopLayout } } : {}),
      ...(needsMobileFork ? { mobile: { ...priorDesktopLayout } } : {}),
    },
  };
}

/** Copy desktop layout into tablet/mobile slots when they are missing. */
export function ensureResponsiveLayoutSlots(block: CanvasSectionBlock): CanvasSectionBlock {
  const responsive = block.responsiveLayout ?? {};
  if (responsive.tablet && responsive.mobile) return block;
  return {
    ...block,
    responsiveLayout: {
      ...responsive,
      ...(responsive.tablet ? {} : { tablet: { ...block.layout } }),
      ...(responsive.mobile ? {} : { mobile: { ...block.layout } }),
    },
  };
}

export function resolveRuntimeViewport(adminViewport?: ResponsiveViewport) {
  return (
    adminViewport ??
    (typeof window === 'undefined'
      ? 'desktop'
      : resolveResponsiveLayoutBreakpoint(window.innerWidth))
  );
}

export function finiteStyleNumber(value: unknown, fallback: number) {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function clampStyleNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

export function resolveResponsiveBlockTransform(
  style: Record<string, unknown> | undefined,
  viewport: ResponsiveViewport,
  options?: { isolateAdminViewport?: boolean }
) {
  const styleRecord = style && typeof style === 'object' && !Array.isArray(style) ? style : {};
  const baseTransform =
    styleRecord.transform && typeof styleRecord.transform === 'object' && !Array.isArray(styleRecord.transform)
      ? (styleRecord.transform as Record<string, unknown>)
      : {};
  const responsiveRoot =
    styleRecord.responsiveTransform &&
    typeof styleRecord.responsiveTransform === 'object' &&
    !Array.isArray(styleRecord.responsiveTransform)
      ? (styleRecord.responsiveTransform as Record<string, unknown>)
      : {};
  const slot = viewport !== 'desktop' ? responsiveRoot[viewport] : undefined;
  const isolateAdminViewport = options?.isolateAdminViewport === true;
  const source =
    viewport === 'desktop'
      ? baseTransform
      : slot && typeof slot === 'object' && !Array.isArray(slot)
        ? (slot as Record<string, unknown>)
        : isolateAdminViewport
          ? {}
          : baseTransform;
  return {
    offsetX: clampStyleNumber(
      finiteStyleNumber(source.offsetX, 0),
      -FREE_POSITION_OFFSET_LIMIT_PERCENT,
      FREE_POSITION_OFFSET_LIMIT_PERCENT
    ),
    offsetY: clampStyleNumber(
      finiteStyleNumber(source.offsetY, 0),
      -FREE_POSITION_OFFSET_LIMIT_PERCENT,
      FREE_POSITION_OFFSET_LIMIT_PERCENT
    ),
    offsetXPx: Number.isFinite(Number(source.offsetXPx)) ? Number(source.offsetXPx) : null,
    offsetYPx: Number.isFinite(Number(source.offsetYPx)) ? Number(source.offsetYPx) : null,
    widthScale: clampStyleNumber(finiteStyleNumber(source.widthScale, 1), 0.2, 2),
    heightScale: clampStyleNumber(finiteStyleNumber(source.heightScale, 1), 0.2, 2),
  };
}

export function resolveBuilderBlockLabel(block: CanvasSectionBlock) {
  const content = block.content && typeof block.content === 'object' ? block.content : {};
  const style = block.style && typeof block.style === 'object' ? block.style : {};
  const explicit =
    typeof content.builderLabel === 'string' && content.builderLabel.trim()
      ? content.builderLabel.trim()
      : typeof style.builderLabel === 'string' && style.builderLabel.trim()
        ? style.builderLabel.trim()
        : '';
  if (explicit) return explicit;

  const label = typeof content.label === 'string' ? content.label.trim() : '';
  if (label) return label;

  const text = typeof content.text === 'string' ? content.text.replace(/\s+/g, ' ').trim() : '';
  if (text) return text.length > 34 ? `${text.slice(0, 31)}...` : text;

  return block.id
    .replace(/^canvas-/, '')
    .replace(/^holding-/, '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

export function resolveBuilderBlockStyleMeta(block: CanvasSectionBlock) {
  return block.style && typeof block.style === 'object' && !Array.isArray(block.style)
    ? (block.style as Record<string, unknown>)
    : {};
}

export function isBuilderLayerLocked(block: CanvasSectionBlock) {
  return resolveBuilderBlockStyleMeta(block).builderLocked === true;
}

export function isBuilderLayerHidden(block: CanvasSectionBlock) {
  return resolveBuilderBlockStyleMeta(block).builderHidden === true;
}
